import { Link } from "@tanstack/react-router";
import {
  Building,
  ChefHat,
  DollarSign,
  Pencil,
  Percent,
  Receipt,
  Soup,
  Tag,
  UtensilsCrossed,
} from "lucide-react";
import { useLingui } from "@lingui/react/macro";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { InfoCard } from "@/components/common/InfoCard";
import { formatCurrency } from "@/components/recipes/cost/CostValue";
import { useCostSummary } from "@/hooks/recipes";
import { useMenusForItems } from "@/hooks/menu-items";
import type { MenuItem } from "@/types/menu-item";
import { foodCostPercent } from "./menu-item-cost";
import { BackToMenuItemsLink } from "./BackToMenuItemsLink";

interface MenuItemDetailViewProps {
  item: MenuItem;
  canEdit: boolean;
  onEdit: () => void;
}

/**
 * One menu item: what it is, what it sells for and what it costs to make. The
 * Edit button reuses the same form the list opens, via the page that owns it.
 */
export function MenuItemDetailView({ item, canEdit, onEdit }: MenuItemDetailViewProps) {
  const { t } = useLingui();
  const dish = item.recipes[0];
  const dishId = dish?.recipeId ?? 0;
  const { data: cost } = useCostSummary(dishId, dishId > 0);
  const { data: menus } = useMenusForItems();
  const menuName = menus?.data.find((m) => String(m.id) === String(item.menuId))?.name;

  const price = item.price != null && !Number.isNaN(Number(item.price)) ? Number(item.price) : null;
  const costPerPortion = cost?.costPerPortion ?? null;
  const percent = foodCostPercent(costPerPortion, price);
  const concepts = item.concepts?.map((c) => c.company?.name).filter(Boolean).join(", ");

  return (
    <div className="flex flex-col gap-5 px-3">
      <div className="flex flex-col gap-3">
        <BackToMenuItemsLink />
        <div className="flex items-end justify-between gap-4">
          <div className="flex items-center gap-3">
            <h1 className="text-4xl leading-none font-semibold tracking-tight text-(--color-text-primary)">
              {item.name}
            </h1>
            <Badge variant={item.isAvailable ? "success" : "secondary"}>
              {item.isAvailable ? t`Available` : t`Unavailable`}
            </Badge>
          </div>
          {canEdit && (
            <Button variant="brand-outline" onClick={onEdit} className="shrink-0">
              <Pencil className="size-4" />
              {t`Edit`}
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <InfoCard icon={<DollarSign className="size-4" />} label={t`Price`} value={formatCurrency(price)} />
        <InfoCard
          icon={<Receipt className="size-4" />}
          label={t`Cost per plate`}
          value={
            <>
              {formatCurrency(costPerPortion)}
              {cost && costPerPortion != null && cost.isComplete === false ? (
                <span className="block text-xs font-normal text-(--color-text-secondary)">
                  {t`Minimum, recipe costing is incomplete`}
                </span>
              ) : null}
            </>
          }
        />
        <InfoCard
          icon={<Percent className="size-4" />}
          label={t`Food cost`}
          value={percent != null ? `${percent.toFixed(1)}%` : "—"}
        />
        <InfoCard icon={<Tag className="size-4" />} label={t`Category`} value={item.menuCategory?.name ?? item.category ?? "—"} />
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <InfoCard icon={<UtensilsCrossed className="size-4" />} label={t`Menu`} value={menuName ?? "—"} />
        <InfoCard icon={<Building className="size-4" />} label={t`Concepts`} value={concepts || "—"} />
        <InfoCard icon={<ChefHat className="size-4" />} label={t`Station`} value={item.station || "—"} />
        <InfoCard
          icon={<Soup className="size-4" />}
          label={t`Recipe`}
          value={
            dish ? (
              <Link
                to="/recipes/$recipeId"
                params={{ recipeId: String(dish.recipeId) }}
                className="text-(--color-action-fg) hover:underline"
              >
                {dish.recipe?.name ?? t`Recipe #${dish.recipeId}`}
              </Link>
            ) : (
              "—"
            )
          }
        />
      </div>

      {item.description ? (
        <p className="text-sm text-(--color-text-secondary)">{item.description}</p>
      ) : null}
    </div>
  );
}
